import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { ArrowLeft, Clock, Calendar, User, Quote, BookOpen, Brain, Target, Lightbulb, Timer, BookMarked, Bookmark } from "lucide-react";
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import CTACard from "@/components/blog/CTACard";
import BlogImage from "@/components/blog/BlogImage";
import ScrollToTopButton from "@/components/ScrollToTopButton";
import { getPostBySlug } from "@/data/blogPosts";
const Leia52LivrosPorAno = () => {
  const post = getPostBySlug("leia-52-livros-por-ano") || {
    metaTitle: "Como Ler 52 Livros Por Ano: Guia Prático | HabitFrog",
    metaDescription: "Aprenda técnicas comprovadas para ler um livro por semana. Descubra como criar o hábito de leitura e transformar sua vida através dos livros.",
    category: "Aprendizado",
    author: "Equipe HabitFrog",
    date: "10 Dez 2025",
    readTime: "25 min de leitura",
    slug: "leia-52-livros-por-ano",
    title: "Leia 52 Livros Por Ano"
  };

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>{post.metaTitle}</title>
        <meta name="description" content={post.metaDescription} />
        <meta property="og:title" content={post.metaTitle} />
        <meta property="og:description" content={post.metaDescription} />
        <meta property="og:type" content="article" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={post.metaTitle} />
        <meta name="twitter:description" content={post.metaDescription} />
        <link rel="canonical" href={`https://habitfrog.com/blog/${post.slug}`} />
        <meta name="keywords" content="ler 52 livros por ano, como ler mais, hábito de leitura, leitura rápida, ler um livro por semana, como criar hábito de leitura, dicas de leitura, desafio de leitura" />
      </Helmet>

      <Header />
      <ScrollToTopButton />

      <main className="pt-24 pb-16">
        <div className="container mx-auto px-4">
          <nav className="mb-8" aria-label="Breadcrumb">
            <Link to="/home" className="inline-flex items-center gap-2 text-muted-foreground hover:text-primary transition-colors">
              <ArrowLeft className="w-4 h-4" />
              Voltar ao início
            </Link>
          </nav>

          <header className="max-w-4xl mx-auto mb-12">
            <span className="inline-block bg-primary/20 text-primary px-3 py-1 rounded-full text-sm font-semibold mb-4">
              {post.category}
            </span>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-display font-bold text-foreground mb-6 leading-tight">
              Leia 52 Livros Por Ano: O Guia Completo Para Transformar Sua Vida Através da Leitura
            </h1>
            <p className="text-xl text-muted-foreground mb-6">
              Técnicas práticas, histórias inspiradoras e estratégias comprovadas para ler um livro por semana e absorver conhecimento que vai mudar sua trajetória
            </p>
            <div className="flex flex-wrap items-center gap-4 text-muted-foreground">
              <div className="flex items-center gap-2">
                <User className="w-4 h-4" />
                <span>{post.author}</span>
              </div>
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                <span>{post.date}</span>
              </div>
              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4" />
                <span>{post.readTime}</span>
              </div>
            </div>
          </header>

          <article className="max-w-4xl mx-auto prose prose-lg max-w-none">
            {/* Introdução */}
            <div className="bg-gradient-to-r from-primary/10 via-accent/10 to-secondary/20 p-8 rounded-2xl border mb-12">
              <div className="flex items-start gap-4">
                <BookOpen className="w-8 h-8 text-primary flex-shrink-0 mt-1" />
                <div>
                  <p className="text-xl text-foreground leading-relaxed mb-4">
                    Imagine ter acesso às mentes mais brilhantes da história da humanidade – filósofos, cientistas, empresários, artistas – e poder aprender diretamente com eles. Essa é a magia dos livros. E se eu te disser que é possível ler 52 livros por ano – um por semana – sem precisar abrir mão do seu trabalho, família ou lazer?
                  </p>
                  <p className="text-muted-foreground">
                    Neste guia completo, vou te mostrar exatamente como criar o hábito de leitura consistente, com técnicas práticas usadas por CEOs, empreendedores de sucesso e os maiores pensadores do nosso tempo.
                  </p>
                </div>
              </div>
            </div>

            <div className="mb-12">
              <p className="text-xl text-muted-foreground leading-relaxed">
                Warren Buffett lê 500 páginas por dia. Bill Gates lê 50 livros por ano. Mark Zuckerberg fez um desafio público de ler um livro a cada duas semanas. Elon Musk atribui grande parte de seu conhecimento em foguetes à leitura obsessiva. Será coincidência que as pessoas mais bem-sucedidas do mundo são também vorazes leitores? Spoiler: não é.
              </p>
            </div>

            <BlogImage 
              src="/images/blog/mulher-estudando-livros.jpg" 
              alt="Pessoa estudando com vários livros abertos na mesa, representando o desafio de ler 52 livros por ano e o hábito de leitura consistente" 
              className="h-64 md:h-80 mb-12"
              priority
            />

            {/* A História de Pedro */}
            <section className="mb-16">
              <h2 className="text-3xl font-bold text-foreground mb-6 flex items-center gap-4">
                <BookMarked className="w-10 h-10 text-primary" />
                A História de Pedro: De Zero Livros Para 52 Por Ano
              </h2>

              <div className="bg-gradient-to-r from-primary/10 via-accent/10 to-secondary/20 p-8 rounded-2xl border mb-8">
                <div className="flex items-start gap-4">
                  <Quote className="w-8 h-8 text-primary flex-shrink-0 mt-1" />
                  <div>
                    <p className="text-xl text-foreground italic leading-relaxed mb-4">
                      "Eu sempre me considerava uma pessoa inteligente, mas estava estagnado. Mesma posição no trabalho há três anos, mesmas conversas, mesmas ideias. Um dia, meu chefe me perguntou qual foi o último livro que li. Fiquei vermelho. Não lembrava. Fazia mais de cinco anos que não terminava um livro."
                    </p>
                    <p className="text-muted-foreground font-semibold">— Pedro, 32 anos, Analista de Marketing, Belo Horizonte</p>
                  </div>
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-8 mb-8">
                <div>
                  <h3 className="text-xl font-bold text-foreground mb-4">O Despertador que Mudou Tudo</h3>
                  <p className="text-foreground/80 mb-4">
                    A pergunta do chefe ficou martelando na cabeça de Pedro por semanas. Ele começou a perceber que seus colegas que cresciam na empresa eram justamente os que sempre tinham um livro novo para recomendar, uma ideia diferente para compartilhar, uma perspectiva única sobre os problemas.
                  </p>
                  <p className="text-foreground/80 mb-4">
                    "Eu percebi que estava preso numa bolha. Minhas únicas fontes de informação eram redes sociais e séries na Netflix. Meu cérebro estava sendo alimentado com entretenimento raso, enquanto meus concorrentes consumiam conhecimento profundo através da leitura."
                  </p>
                  <p className="text-foreground/80">
                    Pedro decidiu fazer uma mudança radical. Estabeleceu uma meta ambiciosa: ler 52 livros por ano – um por semana. Muitos disseram que era impossível. Ele provou que estavam errados.
                  </p>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-foreground mb-4">Os Primeiros Passos da Jornada</h3>
                  <p className="text-foreground/80 mb-4">
                    No começo, Pedro cometeu todos os erros possíveis. Comprou cinco livros de uma vez, todos complexos e longos. Tentou ler de noite, depois de um dia exaustivo de trabalho. Resultado: dormia na terceira página.
                  </p>
                  <p className="text-foreground/80 mb-4">
                    "Nas primeiras duas semanas, li apenas 30 páginas. Estava desanimado, achando que não era 'feito' para ler. Mas então descobri que o problema não era eu – era minha estratégia. Eu estava tentando criar o hábito de leitura da forma errada."
                  </p>
                  <p className="text-foreground/80">
                    Foi quando Pedro começou a pesquisar sobre formação de hábitos e encontrou técnicas que transformariam completamente sua relação com os livros.
                  </p>
                </div>
              </div>

              <BlogImage 
                src="/images/blog/cafe-leitura.jpg" 
                alt="Pessoa lendo livro com café, representando a transformação através do hábito de ler 52 livros por ano" 
                className="h-64 md:h-80 mb-8"
              />

              <div className="bg-primary/10 p-6 rounded-xl border-l-4 border-primary mb-8">
                <h3 className="font-bold text-foreground mb-2">O Resultado Após Um Ano</h3>
                <p className="text-foreground/80 mb-4">
                  Pedro não apenas atingiu a meta de 52 livros – ele superou, lendo 61 livros no primeiro ano. Mas os números são apenas parte da história.
                </p>
                <p className="text-foreground/80">
                  "Fui promovido. Duas vezes. Minhas ideias nas reuniões passaram a ser valorizadas. Minha capacidade de resolver problemas complexos multiplicou. Meus relacionamentos melhoraram porque eu tinha mais empatia, mais repertório. Ler 52 livros por ano não mudou apenas o que eu sabia – mudou quem eu era."
                </p>
              </div>
            </section>

            {/* Por Que 52 Livros? A Matemática Por Trás */}
            <section className="mb-16">
              <h2 className="text-3xl font-bold text-foreground mb-6 flex items-center gap-4">
                <Brain className="w-10 h-10 text-accent" />
                A Matemática Surpreendente de Ler 52 Livros Por Ano
              </h2>

              <p className="text-foreground/80 mb-8">
                Antes de entrarmos nas técnicas práticas, vamos desmistificar essa meta que parece impossível. Quando as pessoas ouvem "52 livros por ano", a reação imediata é: "Isso é loucura, não tenho tempo!" Mas vamos fazer as contas juntos.
              </p>

              <div className="bg-card p-8 rounded-2xl border mb-8">
                <h3 className="text-2xl font-bold text-foreground mb-6 text-center">A Conta que Vai Mudar Sua Perspectiva</h3>
                
                <div className="grid md:grid-cols-2 gap-8 mb-8">
                  <div className="space-y-4">
                    <div className="flex items-center gap-4 p-4 bg-primary/5 rounded-lg">
                      <div className="text-3xl font-bold text-primary">200</div>
                      <div className="text-foreground/80">páginas (média de um livro)</div>
                    </div>
                    <div className="flex items-center gap-4 p-4 bg-primary/5 rounded-lg">
                      <div className="text-3xl font-bold text-primary">÷ 7</div>
                      <div className="text-foreground/80">dias da semana</div>
                    </div>
                    <div className="flex items-center gap-4 p-4 bg-primary/5 rounded-lg">
                      <div className="text-3xl font-bold text-primary">= 29</div>
                      <div className="text-foreground/80">páginas por dia</div>
                    </div>
                  </div>
                  <div className="space-y-4">
                    <div className="flex items-center gap-4 p-4 bg-accent/10 rounded-lg">
                      <div className="text-3xl font-bold text-accent">29</div>
                      <div className="text-foreground/80">páginas por dia</div>
                    </div>
                    <div className="flex items-center gap-4 p-4 bg-accent/10 rounded-lg">
                      <div className="text-3xl font-bold text-accent">× 2</div>
                      <div className="text-foreground/80">minutos por página (leitura normal)</div>
                    </div>
                    <div className="flex items-center gap-4 p-4 bg-accent/10 rounded-lg">
                      <div className="text-3xl font-bold text-accent">= 58</div>
                      <div className="text-foreground/80">minutos por dia</div>
                    </div>
                  </div>
                </div>

                <div className="bg-primary/20 p-6 rounded-xl text-center">
                  <p className="text-2xl font-bold text-foreground mb-2">
                    Menos de 1 hora por dia = 52 livros por ano
                  </p>
                  <p className="text-muted-foreground">
                    Quanto tempo você gasta por dia nas redes sociais? O brasileiro médio passa 3h46min por dia. E se você usasse apenas 1 hora disso para ler?
                  </p>
                </div>
              </div>

              <p className="text-foreground/80 mb-4">
                Agora, a pergunta que realmente importa não é "tenho tempo?", mas sim "como priorizo meu tempo?". Ler 52 livros por ano exige apenas 30 a 60 minutos diários de leitura focada. É menos tempo do que a maioria das pessoas passa scrollando feeds de redes sociais ou assistindo vídeos aleatórios.
              </p>

              <p className="text-foreground/80 mb-8">
                E aqui está o segredo que os grandes leitores conhecem: não é sobre encontrar tempo, é sobre criar o hábito de leitura em momentos que já existem na sua rotina. Vamos explorar exatamente como fazer isso.
              </p>
            </section>

            <div className="my-16"><CTACard variant="primary" /></div>

            {/* As 7 Técnicas Para Ler 52 Livros */}
            <section className="mb-16">
              <h2 className="text-3xl font-bold text-foreground mb-6 flex items-center gap-4">
                <Target className="w-10 h-10 text-primary" />
                As 7 Técnicas Comprovadas Para Ler 52 Livros Por Ano
              </h2>

              <p className="text-foreground/80 mb-8">
                Depois de entrevistar dezenas de pessoas que lêem mais de 50 livros por ano e estudar a rotina de grandes leitores, identifiquei sete técnicas que se repetem consistentemente. Estas não são teorias – são práticas testadas e aprovadas por quem realmente consegue manter o hábito de leitura.
              </p>

              {/* Técnica 1 */}
              <div className="bg-gradient-to-r from-card to-primary/10 p-8 rounded-2xl border mb-8">
                <div className="flex items-start gap-4 mb-6">
                  <div className="bg-primary text-primary-foreground w-12 h-12 rounded-full flex items-center justify-center font-bold text-xl flex-shrink-0">1</div>
                  <div>
                    <h3 className="text-2xl font-bold text-foreground mb-2">A Regra dos 20 Páginas Diárias</h3>
                    <p className="text-muted-foreground">O mínimo não-negociável que transforma iniciantes em leitores vorazes</p>
                  </div>
                </div>
                <p className="text-foreground/80 mb-4">
                  Esqueça metas grandiosas no começo. A técnica mais eficaz para criar o hábito de ler 52 livros por ano é estabelecer um mínimo diário tão pequeno que seria ridículo não cumprir: 20 páginas por dia.
                </p>
                <p className="text-foreground/80 mb-4">
                  Vinte páginas levam aproximadamente 30 minutos para ler em ritmo normal. É tempo suficiente para avançar significativamente, mas curto o bastante para caber até nos dias mais caóticos. O segredo é que esse mínimo seja absolutamente não-negociável – como escovar os dentes.
                </p>
                <p className="text-foreground/80 mb-4">
                  O poder desta técnica está na consistência composta: 20 páginas por dia × 365 dias = 7.300 páginas por ano = aproximadamente 36-40 livros, dependendo do tamanho médio. E isso é apenas o mínimo. Na prática, muitos dias você lerá mais, porque uma vez que começa, é difícil parar.
                </p>
                <div className="bg-primary/10 p-4 rounded-lg border border-primary/30">
                  <p className="text-sm font-medium text-foreground">
                    <strong>Dica prática:</strong> Deixe seu livro atual sempre no mesmo lugar – na mesa de cabeceira, na bolsa, no escritório. Elimine qualquer fricção entre você e a leitura. Quanto mais fácil for começar, mais você lerá.
                  </p>
                </div>
              </div>

              {/* Técnica 2 */}
              <div className="bg-gradient-to-r from-card to-accent/10 p-8 rounded-2xl border mb-8">
                <div className="flex items-start gap-4 mb-6">
                  <div className="bg-accent text-accent-foreground w-12 h-12 rounded-full flex items-center justify-center font-bold text-xl flex-shrink-0">2</div>
                  <div>
                    <h3 className="text-2xl font-bold text-foreground mb-2">Stack de Hábitos: Associe Leitura a Rotinas Existentes</h3>
                    <p className="text-muted-foreground">Use o poder do empilhamento de hábitos para tornar a leitura automática</p>
                  </div>
                </div>
                <p className="text-foreground/80 mb-4">
                  Uma das técnicas mais poderosas da ciência comportamental é o "habit stacking" ou empilhamento de hábitos. A ideia é simples: conecte o novo hábito (leitura) a um hábito já existente e consolidado.
                </p>
                <div className="grid md:grid-cols-2 gap-6 mb-4">
                  <div className="bg-background/50 p-4 rounded-lg">
                    <p className="font-semibold text-foreground mb-2">Antes:</p>
                    <ul className="text-sm text-foreground/80 space-y-1">
                      <li>• Acordar → Checar celular → Redes sociais</li>
                      <li>• Almoço → Comer assistindo algo → Voltar ao trabalho</li>
                      <li>• Noite → Netflix → Dormir</li>
                    </ul>
                  </div>
                  <div className="bg-background/50 p-4 rounded-lg">
                    <p className="font-semibold text-foreground mb-2">Depois:</p>
                    <ul className="text-sm text-foreground/80 space-y-1">
                      <li>• Acordar → Café → <strong>20 páginas</strong> → Trabalho</li>
                      <li>• Almoço → Comer → <strong>10 minutos de leitura</strong></li>
                      <li>• Noite → <strong>Ler</strong> → Dormir</li>
                    </ul>
                  </div>
                </div>
                <p className="text-foreground/80 mb-4">
                  O segredo é escolher um "gatilho" específico. "Depois de [hábito existente], eu vou [ler por X minutos/páginas]." Quanto mais específico, melhor. "Depois de me sentar no sofá às 21h" é melhor que "ler à noite".
                </p>
                <p className="text-foreground/80">
                  Pedro, nossa história do início, usou exatamente essa técnica: "Depois de ligar a cafeteira de manhã, eu leio enquanto o café fica pronto." Eram apenas 7 minutos, mas aconteciam todos os dias, sem exceção.
                </p>
              </div>

              {/* Técnica 3 */}
              <div className="bg-gradient-to-r from-card to-secondary/20 p-8 rounded-2xl border mb-8">
                <div className="flex items-start gap-4 mb-6">
                  <div className="bg-secondary text-secondary-foreground w-12 h-12 rounded-full flex items-center justify-center font-bold text-xl flex-shrink-0">3</div>
                  <div>
                    <h3 className="text-2xl font-bold text-foreground mb-2">A Regra do Múltiplo Formato</h3>
                    <p className="text-muted-foreground">Livro físico, e-book, audiobook – use todos a seu favor</p>
                  </div>
                </div>
                <p className="text-foreground/80 mb-4">
                  Pessoas que lêem 52 livros por ano raramente usam apenas um formato. Elas são estratégicas: usam o formato que faz mais sentido para cada situação.
                </p>
                <div className="grid md:grid-cols-3 gap-4 mb-4">
                  <div className="bg-card p-4 rounded-lg border">
                    <div className="text-3xl mb-2">📖</div>
                    <h4 className="font-semibold text-foreground mb-2">Livro Físico</h4>
                    <p className="text-sm text-muted-foreground">Ideal para: leitura focada em casa, livros técnicos com gráficos, momentos de relaxamento</p>
                  </div>
                  <div className="bg-card p-4 rounded-lg border">
                    <div className="text-3xl mb-2">📱</div>
                    <h4 className="font-semibold text-foreground mb-2">E-book (Kindle/App)</h4>
                    <p className="text-sm text-muted-foreground">Ideal para: viagens, filas, transporte público, leitura no escuro</p>
                  </div>
                  <div className="bg-card p-4 rounded-lg border">
                    <div className="text-3xl mb-2">🎧</div>
                    <h4 className="font-semibold text-foreground mb-2">Audiobook</h4>
                    <p className="text-sm text-muted-foreground">Ideal para: trânsito, exercícios, tarefas domésticas, caminhadas</p>
                  </div>
                </div>
                <p className="text-foreground/80 mb-4">
                  Uma estratégia poderosa: leia o mesmo livro em múltiplos formatos. Comece no físico em casa, continue no Kindle no metrô, e ouça o audiobook na academia. Isso pode dobrar ou triplicar seu tempo de leitura diário sem exigir tempo adicional.
                </p>
                <div className="bg-secondary/20 p-4 rounded-lg border border-secondary/30">
                  <p className="text-sm font-medium text-foreground">
                    <strong>Hack do audiobook:</strong> Ouça em velocidade 1.25x ou 1.5x. A maioria dos narradores lê devagar. Aumentar a velocidade permite consumir mais conteúdo no mesmo tempo, sem perder compreensão. Com prática, você pode até usar 2x.
                  </p>
                </div>
              </div>

              {/* Técnica 4 */}
              <div className="bg-gradient-to-r from-card to-coral/10 p-8 rounded-2xl border mb-8">
                <div className="flex items-start gap-4 mb-6">
                  <div className="bg-coral text-white w-12 h-12 rounded-full flex items-center justify-center font-bold text-xl flex-shrink-0">4</div>
                  <div>
                    <h3 className="text-2xl font-bold text-foreground mb-2">Abandone Livros Sem Culpa</h3>
                    <p className="text-muted-foreground">A regra das 50 páginas que liberta leitores do perfeccionismo</p>
                  </div>
                </div>
                <p className="text-foreground/80 mb-4">
                  Um dos maiores assassinos do hábito de leitura é a culpa de abandonar um livro. Muitas pessoas se forçam a terminar livros que não estão curtindo, e isso transforma a leitura em obrigação torturante.
                </p>
                <p className="text-foreground/80 mb-4">
                  A verdade é: existem mais livros bons do que tempo para lê-los. Se um livro não está te prendendo, você tem total permissão para abandoná-lo. A vida é curta demais para livros chatos.
                </p>
                <div className="bg-coral/10 p-6 rounded-xl border-l-4 border-coral mb-4">
                  <h4 className="font-bold text-foreground mb-2">A Regra das 50 Páginas (Nancy Pearl)</h4>
                  <p className="text-foreground/80">
                    Leia 50 páginas. Se não te prendeu, abandone sem culpa. Se você tem menos de 50 anos, a regra se aplica. Se você tem mais de 50, subtraia sua idade de 100 – esse é seu número mágico de páginas antes de abandonar.
                  </p>
                </div>
                <p className="text-foreground/80">
                  Leitores produtivos são implacáveis com seu tempo. Eles sabem que cada minuto gasto com um livro medíocre é um minuto que não estão investindo em um livro excelente. Crie uma lista de "abandonados" sem vergonha – ela é sinal de que você valoriza seu tempo.
                </p>
              </div>

              {/* Técnica 5 */}
              <div className="bg-gradient-to-r from-card to-primary/10 p-8 rounded-2xl border mb-8">
                <div className="flex items-start gap-4 mb-6">
                  <div className="bg-primary text-primary-foreground w-12 h-12 rounded-full flex items-center justify-center font-bold text-xl flex-shrink-0">5</div>
                  <div>
                    <h3 className="text-2xl font-bold text-foreground mb-2">Leia Múltiplos Livros Simultaneamente</h3>
                    <p className="text-muted-foreground">Por que ter apenas um livro é a receita para o tédio</p>
                  </div>
                </div>
                <p className="text-foreground/80 mb-4">
                  Parece contraproducente, mas ler 2-4 livros simultaneamente pode acelerar significativamente seu ritmo de leitura. O segredo é que diferentes momentos pedem diferentes tipos de conteúdo.
                </p>
                <div className="grid md:grid-cols-2 gap-6 mb-4">
                  <div className="space-y-4">
                    <h4 className="font-semibold text-foreground">Sistema de Rotação Sugerido:</h4>
                    <ul className="space-y-3 text-foreground/80">
                      <li className="flex items-start gap-2">
                        <span className="text-primary font-bold">1.</span>
                        <span><strong>Livro "principal"</strong> (não-ficção densa) – para sessões focadas de manhã ou à noite</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-primary font-bold">2.</span>
                        <span><strong>Livro "leve"</strong> (ficção ou narrativa) – para antes de dormir ou momentos de relaxamento</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-primary font-bold">3.</span>
                        <span><strong>Audiobook</strong> – para trânsito, exercícios, tarefas</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-primary font-bold">4.</span>
                        <span><strong>E-book curto</strong> – para momentos de espera (consultório, fila)</span>
                      </li>
                    </ul>
                  </div>
                  <BlogImage 
                    src="/images/blog/biblioteca-livros.jpg" 
                    alt="Pilha de livros diversos representando leitura simultânea de múltiplos títulos para ler 52 livros por ano" 
                    className="h-48"
                  />
                </div>
                <p className="text-foreground/80">
                  Essa abordagem elimina o problema de "não estar no clima" para determinado livro. Não quer ler algo técnico agora? Pegue o romance. Está fazendo faxina? Coloque o audiobook. Sempre haverá um livro adequado para o momento.
                </p>
              </div>

              {/* Técnica 6 */}
              <div className="bg-gradient-to-r from-card to-accent/10 p-8 rounded-2xl border mb-8">
                <div className="flex items-start gap-4 mb-6">
                  <div className="bg-accent text-accent-foreground w-12 h-12 rounded-full flex items-center justify-center font-bold text-xl flex-shrink-0">6</div>
                  <div>
                    <h3 className="text-2xl font-bold text-foreground mb-2">Crie um Sistema de Tracking Visual</h3>
                    <p className="text-muted-foreground">O poder da gamificação no hábito de leitura</p>
                  </div>
                </div>
                <p className="text-foreground/80 mb-4">
                  O que é medido, melhora. E o que é medido visualmente, melhora dramaticamente. Criar um sistema de acompanhamento visual do seu progresso de leitura ativa os centros de recompensa do cérebro e mantém você motivado.
                </p>
                <div className="grid md:grid-cols-3 gap-4 mb-4">
                  <div className="bg-card p-4 rounded-lg border text-center">
                    <div className="text-4xl mb-2">📊</div>
                    <h4 className="font-semibold text-foreground mb-2">Goodreads</h4>
                    <p className="text-sm text-muted-foreground">Defina meta anual e veja seu progresso</p>
                  </div>
                  <div className="bg-card p-4 rounded-lg border text-center">
                    <div className="text-4xl mb-2">📓</div>
                    <h4 className="font-semibold text-foreground mb-2">Bullet Journal</h4>
                    <p className="text-sm text-muted-foreground">Preencha quadradinhos para cada livro lido</p>
                  </div>
                  <div className="bg-card p-4 rounded-lg border text-center">
                    <div className="text-4xl mb-2">🐸</div>
                    <h4 className="font-semibold text-foreground mb-2">HabitFrog</h4>
                    <p className="text-sm text-muted-foreground">Evolua seu sapo com cada dia de leitura</p>
                  </div>
                </div>
                <p className="text-foreground/80 mb-4">
                  O elemento visual é crucial. Ver uma barra de progresso crescer, quadradinhos sendo preenchidos, ou um avatar evoluindo cria dopamina e reforça o comportamento. Não subestime o poder da gamificação – ela funciona porque nosso cérebro foi programado para buscar recompensas visuais de progresso.
                </p>
                <p className="text-foreground/80">
                  Pedro, nossa história do início, usava um pôster na parede com 52 quadrados. Cada livro terminado ganhava uma etiqueta colorida no quadrado correspondente. "No final do ano, olhar para aquele pôster completamente preenchido foi uma das maiores satisfações da minha vida", conta.
                </p>
              </div>

              {/* Técnica 7 */}
              <div className="bg-gradient-to-r from-card to-secondary/20 p-8 rounded-2xl border mb-8">
                <div className="flex items-start gap-4 mb-6">
                  <div className="bg-secondary text-secondary-foreground w-12 h-12 rounded-full flex items-center justify-center font-bold text-xl flex-shrink-0">7</div>
                  <div>
                    <h3 className="text-2xl font-bold text-foreground mb-2">Participe de uma Comunidade de Leitores</h3>
                    <p className="text-muted-foreground">Responsabilidade social é o acelerador secreto</p>
                  </div>
                </div>
                <p className="text-foreground/80 mb-4">
                  Hábitos são mais fáceis de manter quando fazem parte de uma identidade social. Quando você se vê como "alguém que lê", e é visto assim pelos outros, abandonar o hábito se torna muito mais difícil.
                </p>
                <ul className="space-y-4 mb-4">
                  <li className="flex items-start gap-3">
                    <Bookmark className="w-5 h-5 text-primary flex-shrink-0 mt-1" />
                    <div>
                      <strong className="text-foreground">Clubes de leitura</strong>
                      <p className="text-sm text-muted-foreground">Grupos presenciais ou online que lêem e discutem o mesmo livro. A pressão social positiva mantém você lendo.</p>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <Bookmark className="w-5 h-5 text-primary flex-shrink-0 mt-1" />
                    <div>
                      <strong className="text-foreground">Goodreads e redes sociais literárias</strong>
                      <p className="text-sm text-muted-foreground">Compartilhe suas leituras, veja o que outros estão lendo, descubra recomendações.</p>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <Bookmark className="w-5 h-5 text-primary flex-shrink-0 mt-1" />
                    <div>
                      <strong className="text-foreground">Parceiros de accountability</strong>
                      <p className="text-sm text-muted-foreground">Um amigo com quem você reporta semanalmente seu progresso de leitura.</p>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <Bookmark className="w-5 h-5 text-primary flex-shrink-0 mt-1" />
                    <div>
                      <strong className="text-foreground">Desafios públicos</strong>
                      <p className="text-sm text-muted-foreground">Declare sua meta publicamente. A vergonha de falhar em público é um motivador poderoso.</p>
                    </div>
                  </li>
                </ul>
                <p className="text-foreground/80">
                  Estudos mostram que pessoas que fazem parte de comunidades têm 65% mais chances de manter hábitos a longo prazo. Não subestime o poder de ter pessoas ao seu redor que compartilham seus valores e objetivos.
                </p>
              </div>
            </section>

            <BlogImage 
              src="/images/blog/equipe-colaborativa.jpg" 
              alt="Grupo de pessoas em clube de leitura discutindo livros, representando comunidade de leitores" 
              className="h-64 md:h-80 mb-12"
            />

            {/* O Que Grandes Leitores Fazem Diferente */}
            <section className="mb-16">
              <h2 className="text-3xl font-bold text-foreground mb-6 flex items-center gap-4">
                <Lightbulb className="w-10 h-10 text-coral" />
                O Que os Maiores Leitores do Mundo Fazem Diferente
              </h2>

              <p className="text-foreground/80 mb-8">
                Estudando a rotina de CEOs, bilionários e intelectuais que são conhecidos por sua voracidade leitora, alguns padrões se repetem consistentemente. Veja o que podemos aprender com eles sobre como ler 52 livros por ano.
              </p>

              {/* Bill Gates */}
              <div className="bg-gradient-to-r from-card to-primary/10 p-8 rounded-2xl border mb-8">
                <div className="grid md:grid-cols-4 gap-6">
                  <div className="md:col-span-1">
                    <BlogImage 
                      src="/images/blog/cafe-leitura.jpg" 
                      alt="Bill Gates, fundador da Microsoft, representando hábitos de leitura de pessoas bem-sucedidas" 
                      className="h-32"
                    />
                  </div>
                  <div className="md:col-span-3">
                    <h3 className="text-2xl font-bold text-foreground mb-4">Bill Gates: 50 Livros Por Ano</h3>
                    <p className="text-foreground/80 mb-4">
                      O fundador da Microsoft é famoso por sua "think week" – semanas inteiras dedicadas apenas à leitura e reflexão. Mas no dia a dia, Gates lê pelo menos uma hora antes de dormir e carrega sempre um livro consigo.
                    </p>
                    <div className="bg-primary/10 p-4 rounded-lg border border-primary/30">
                      <p className="text-sm font-medium text-foreground italic">
                        "Ler é ainda a principal maneira que eu aprendo coisas novas e testo meu entendimento."
                      </p>
                      <p className="text-xs text-muted-foreground mt-2">— Bill Gates</p>
                    </div>
                    <p className="text-foreground/80 mt-4">
                      <strong>Lição:</strong> Gates anota nas margens e faz resumos de cada livro. Ele não apenas lê – processa ativamente a informação.
                    </p>
                  </div>
                </div>
              </div>

              {/* Warren Buffett */}
              <div className="bg-gradient-to-r from-card to-accent/10 p-8 rounded-2xl border mb-8">
                <div className="grid md:grid-cols-4 gap-6">
                  <div className="md:col-span-3">
                    <h3 className="text-2xl font-bold text-foreground mb-4">Warren Buffett: 500 Páginas Por Dia</h3>
                    <p className="text-foreground/80 mb-4">
                      O maior investidor da história dedica 80% do seu dia de trabalho à leitura. Para Buffett, ler é trabalho – é como ele toma decisões melhores.
                    </p>
                    <p className="text-foreground/80 mb-4">
                      No início da carreira, quando um jovem perguntou como poderia ter sucesso como investidor, Buffett apontou para uma pilha de relatórios anuais e disse: "Leia 500 páginas por dia. É assim que o conhecimento funciona. Ele se acumula, como juros compostos."
                    </p>
                    <div className="bg-accent/10 p-4 rounded-lg border border-accent/30">
                      <p className="text-sm font-medium text-foreground italic">
                        "Eu só sento no meu escritório e leio o dia inteiro. Ler é como eu tenho vantagem competitiva."
                      </p>
                      <p className="text-xs text-muted-foreground mt-2">— Warren Buffett</p>
                    </div>
                  </div>
                  <div className="md:col-span-1">
                    <BlogImage 
                      src="/images/blog/leitura-livros.jpg" 
                      alt="Warren Buffett, investidor lendário, representando leitura como ferramenta de sucesso" 
                      className="h-32"
                    />
                  </div>
                </div>
              </div>

              {/* Oprah Winfrey */}
              <div className="bg-gradient-to-r from-card to-coral/10 p-8 rounded-2xl border mb-8">
                <div className="grid md:grid-cols-4 gap-6">
                  <div className="md:col-span-1">
                    <BlogImage 
                      src="/images/blog/biblioteca-livros.jpg" 
                      alt="Oprah Winfrey, apresentadora e empresária, conhecida pelo seu clube do livro" 
                      className="h-32"
                    />
                  </div>
                  <div className="md:col-span-3">
                    <h3 className="text-2xl font-bold text-foreground mb-4">Oprah Winfrey: O Poder do Clube do Livro</h3>
                    <p className="text-foreground/80 mb-4">
                      O Oprah's Book Club se tornou um fenômeno cultural. Qualquer livro escolhido por Oprah automaticamente se tornava bestseller. Mas o mais importante é o que isso nos ensina sobre comunidade e leitura.
                    </p>
                    <p className="text-foreground/80 mb-4">
                      Oprah não apenas lê – ela cria experiências em torno dos livros. Discussões, entrevistas com autores, reflexões pessoais. Para ela, ler é um ato social e transformador.
                    </p>
                    <div className="bg-coral/10 p-4 rounded-lg border border-coral/30">
                      <p className="text-sm font-medium text-foreground italic">
                        "Livros foram minha passagem para a liberdade pessoal."
                      </p>
                      <p className="text-xs text-muted-foreground mt-2">— Oprah Winfrey</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Elon Musk */}
              <div className="bg-gradient-to-r from-card to-secondary/20 p-8 rounded-2xl border mb-8">
                <div className="grid md:grid-cols-4 gap-6">
                  <div className="md:col-span-3">
                    <h3 className="text-2xl font-bold text-foreground mb-4">Elon Musk: Aprendendo Rocket Science Por Livros</h3>
                    <p className="text-foreground/80 mb-4">
                      Quando Musk decidiu criar a SpaceX, ele não tinha formação em engenharia aeroespacial. Sua solução? Ler obsessivamente. Ele consumiu livros-texto de engenharia de foguetes e física orbital até entender o suficiente para liderar a empresa.
                    </p>
                    <p className="text-foreground/80 mb-4">
                      "Eu li muito sobre foguetes", Musk disse em uma entrevista. "Eu li muitos livros." Questionado sobre quais, ele listou dezenas de obras técnicas que estudou página por página.
                    </p>
                    <div className="bg-secondary/20 p-4 rounded-lg border border-secondary/30">
                      <p className="text-sm font-medium text-foreground italic">
                        "É importante ver o conhecimento como uma espécie de árvore semântica. Certifique-se de entender os princípios fundamentais – o tronco e os galhos principais – antes de entrar nas folhas/detalhes."
                      </p>
                      <p className="text-xs text-muted-foreground mt-2">— Elon Musk</p>
                    </div>
                  </div>
                  <div className="md:col-span-1">
                    <BlogImage 
                      src="/images/blog/cafe-leitura.jpg" 
                      alt="Elon Musk, CEO da Tesla e SpaceX, representando aprendizado autodidata através de livros" 
                      className="h-32"
                    />
                  </div>
                </div>
              </div>
            </section>

            <div className="my-16"><CTACard variant="secondary" /></div>

            {/* Erros Comuns a Evitar */}
            <section className="mb-16">
              <h2 className="text-3xl font-bold text-foreground mb-6 flex items-center gap-4">
                <Timer className="w-10 h-10 text-coral" />
                Os 5 Erros Fatais Que Impedem Você de Ler 52 Livros Por Ano
              </h2>

              <p className="text-foreground/80 mb-8">
                Tão importante quanto saber o que fazer é saber o que evitar. Esses são os erros mais comuns que sabotam o hábito de leitura, mesmo em pessoas bem-intencionadas.
              </p>

              <div className="space-y-6 mb-8">
                <div className="bg-red-50 dark:bg-red-950/20 p-6 rounded-xl border border-red-200 dark:border-red-900/30">
                  <h3 className="text-xl font-bold text-foreground mb-3 flex items-center gap-2">
                    <span className="text-red-500">❌</span> Erro 1: Começar com livros muito difíceis
                  </h3>
                  <p className="text-foreground/80 mb-4">
                    Muitas pessoas querem parecer inteligentes e começam com clássicos densos ou obras técnicas complexas. Resultado: frustração e abandono.
                  </p>
                  <div className="bg-background/50 p-4 rounded-lg">
                    <p className="text-sm text-muted-foreground">
                      <strong>Solução:</strong> Comece com livros acessíveis e prazerosos. Construa o hábito primeiro, aumente a complexidade depois. Não há mérito em sofrer para ler – leitura deve ser prazerosa.
                    </p>
                  </div>
                </div>

                <div className="bg-red-50 dark:bg-red-950/20 p-6 rounded-xl border border-red-200 dark:border-red-900/30">
                  <h3 className="text-xl font-bold text-foreground mb-3 flex items-center gap-2">
                    <span className="text-red-500">❌</span> Erro 2: Ler apenas quando "sobra tempo"
                  </h3>
                  <p className="text-foreground/80 mb-4">
                    Tempo nunca "sobra" – ele é investido. Se você espera ter tempo livre para ler, vai esperar para sempre. As demandas da vida sempre expandem para preencher o tempo disponível.
                  </p>
                  <div className="bg-background/50 p-4 rounded-lg">
                    <p className="text-sm text-muted-foreground">
                      <strong>Solução:</strong> Agende a leitura como um compromisso inegociável. Coloque no calendário. Trate como uma reunião importante consigo mesmo.
                    </p>
                  </div>
                </div>

                <div className="bg-red-50 dark:bg-red-950/20 p-6 rounded-xl border border-red-200 dark:border-red-900/30">
                  <h3 className="text-xl font-bold text-foreground mb-3 flex items-center gap-2">
                    <span className="text-red-500">❌</span> Erro 3: Não ter o próximo livro já escolhido
                  </h3>
                  <p className="text-foreground/80 mb-4">
                    Terminar um livro e não ter o próximo em mãos cria uma pausa perigosa. Essa pausa pode durar dias ou semanas, quebrando o momentum.
                  </p>
                  <div className="bg-background/50 p-4 rounded-lg">
                    <p className="text-sm text-muted-foreground">
                      <strong>Solução:</strong> Mantenha uma lista de "próximos livros" e tenha sempre 2-3 já comprados/baixados prontos para começar. Idealmente, comece o próximo no mesmo dia que termina o anterior.
                    </p>
                  </div>
                </div>

                <div className="bg-red-50 dark:bg-red-950/20 p-6 rounded-xl border border-red-200 dark:border-red-900/30">
                  <h3 className="text-xl font-bold text-foreground mb-3 flex items-center gap-2">
                    <span className="text-red-500">❌</span> Erro 4: Competir com a velocidade dos outros
                  </h3>
                  <p className="text-foreground/80 mb-4">
                    "Fulano lê 100 livros por ano, por que eu não consigo?" Essa comparação é tóxica. Velocidade de leitura varia muito, e o que importa é consistência, não velocidade.
                  </p>
                  <div className="bg-background/50 p-4 rounded-lg">
                    <p className="text-sm text-muted-foreground">
                      <strong>Solução:</strong> Compita apenas com seu eu do passado. Se você lia 2 livros por ano e agora lê 20, isso é um crescimento extraordinário – não importa o que os outros fazem.
                    </p>
                  </div>
                </div>

                <div className="bg-red-50 dark:bg-red-950/20 p-6 rounded-xl border border-red-200 dark:border-red-900/30">
                  <h3 className="text-xl font-bold text-foreground mb-3 flex items-center gap-2">
                    <span className="text-red-500">❌</span> Erro 5: Não eliminar distrações digitais
                  </h3>
                  <p className="text-foreground/80 mb-4">
                    Tentar ler com o celular ao lado, notificações ligadas, e a TV de fundo é receita para frustração. A leitura requer foco – e foco é incompatível com multitarefa.
                  </p>
                  <div className="bg-background/50 p-4 rounded-lg">
                    <p className="text-sm text-muted-foreground">
                      <strong>Solução:</strong> Crie um ambiente de leitura. Celular em modo avião ou em outro cômodo. TV desligada. Notificações silenciadas. Trate esses 30-60 minutos como sagrados.
                    </p>
                  </div>
                </div>
              </div>
            </section>

            {/* Plano de Ação */}
            <section className="mb-16">
              <h2 className="text-3xl font-bold text-foreground mb-6 flex items-center gap-4">
                <Target className="w-10 h-10 text-primary" />
                Seu Plano de Ação Para Começar Hoje
              </h2>

              <p className="text-foreground/80 mb-8">
                Conhecimento sem ação é inútil. Aqui está um plano passo a passo para você começar sua jornada de ler 52 livros por ano ainda esta semana.
              </p>

              <div className="bg-card p-8 rounded-2xl border mb-8">
                <h3 className="text-2xl font-bold text-foreground mb-6">Semana 1: Fundação do Hábito de Leitura</h3>
                
                <div className="space-y-4">
                  <div className="flex items-start gap-4 p-4 bg-primary/5 rounded-lg">
                    <div className="bg-primary text-primary-foreground w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm flex-shrink-0">1</div>
                    <div>
                      <h4 className="font-semibold text-foreground">Hoje: Escolha seu primeiro livro</h4>
                      <p className="text-sm text-muted-foreground">Algo que você realmente queira ler. Não o que "deveria" ler – o que genuinamente te interessa. Ficção, não-ficção, o que for.</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4 p-4 bg-primary/5 rounded-lg">
                    <div className="bg-primary text-primary-foreground w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm flex-shrink-0">2</div>
                    <div>
                      <h4 className="font-semibold text-foreground">Hoje: Defina seu horário de leitura</h4>
                      <p className="text-sm text-muted-foreground">Manhã, almoço, noite – qual horário é mais consistente para você? Coloque no calendário como compromisso.</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4 p-4 bg-primary/5 rounded-lg">
                    <div className="bg-primary text-primary-foreground w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm flex-shrink-0">3</div>
                    <div>
                      <h4 className="font-semibold text-foreground">Amanhã: Leia suas primeiras 20 páginas</h4>
                      <p className="text-sm text-muted-foreground">Não mais, não menos. O objetivo é criar consistência, não intensidade. Celebre essa pequena vitória.</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4 p-4 bg-primary/5 rounded-lg">
                    <div className="bg-primary text-primary-foreground w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm flex-shrink-0">4</div>
                    <div>
                      <h4 className="font-semibold text-foreground">Esta semana: Configure seu tracking</h4>
                      <p className="text-sm text-muted-foreground">Crie conta no Goodreads, comece um habit tracker, ou baixe o HabitFrog. Escolha um método e use-o.</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4 p-4 bg-primary/5 rounded-lg">
                    <div className="bg-primary text-primary-foreground w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm flex-shrink-0">5</div>
                    <div>
                      <h4 className="font-semibold text-foreground">Esta semana: Escolha os próximos 3 livros</h4>
                      <p className="text-sm text-muted-foreground">Crie uma lista curta de próximos livros. Compre ou baixe pelo menos um já. Elimine a fricção.</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-gradient-to-r from-primary/20 via-accent/10 to-secondary/20 p-8 rounded-2xl border mb-8">
                <h3 className="text-2xl font-bold text-foreground mb-4 text-center">Sua Meta Progressiva</h3>
                <div className="grid md:grid-cols-4 gap-4 text-center">
                  <div className="bg-card p-4 rounded-lg border">
                    <div className="text-3xl font-bold text-primary mb-2">Mês 1-2</div>
                    <p className="text-sm text-muted-foreground">20 páginas/dia<br />2-3 livros/mês</p>
                  </div>
                  <div className="bg-card p-4 rounded-lg border">
                    <div className="text-3xl font-bold text-primary mb-2">Mês 3-4</div>
                    <p className="text-sm text-muted-foreground">30 páginas/dia<br />3-4 livros/mês</p>
                  </div>
                  <div className="bg-card p-4 rounded-lg border">
                    <div className="text-3xl font-bold text-primary mb-2">Mês 5-6</div>
                    <p className="text-sm text-muted-foreground">40 páginas/dia<br />4-5 livros/mês</p>
                  </div>
                  <div className="bg-card p-4 rounded-lg border">
                    <div className="text-3xl font-bold text-primary mb-2">Ano 1</div>
                    <p className="text-sm text-muted-foreground">40-50 livros<br />Fundação sólida!</p>
                  </div>
                </div>
              </div>
            </section>

            {/* Conclusão */}
            <section className="mb-16">
              <h2 className="text-3xl font-bold text-foreground mb-6">
                Sua Jornada de 52 Livros Começa Com Uma Página
              </h2>

              <div className="bg-gradient-to-r from-primary/10 via-accent/10 to-secondary/20 p-8 rounded-2xl border mb-8">
                <p className="text-xl text-foreground leading-relaxed mb-6">
                  Lembro da história de Pedro, no início deste artigo. Ele passou de anos sem ler um único livro para 61 livros em um ano. Duas promoções depois, ele credita a leitura como a mudança mais importante que fez na vida.
                </p>
                <p className="text-foreground/80 mb-6">
                  "Eu não sou mais a mesma pessoa", Pedro me disse recentemente. "Os livros me deram vocabulário para entender meus problemas, exemplos de pessoas que superaram desafios similares, e ideias que nunca teria sozinho. Cada livro é uma conversa com alguém mais sábio que eu."
                </p>
                <p className="text-foreground/80">
                  Você não precisa ler 52 livros no primeiro ano. Se você ler 12, já será mais do que a maioria dos brasileiros lê em uma década. O importante é começar – e não parar. O hábito de leitura é como uma bola de neve: quanto mais você lê, mais fácil fica ler, e mais você quer ler.
                </p>
              </div>

              <div className="bg-primary/10 p-6 rounded-xl border-l-4 border-primary mb-8">
                <p className="text-lg font-medium text-foreground mb-4">
                  "Um leitor vive mil vidas antes de morrer. O homem que nunca lê vive apenas uma."
                </p>
                <p className="text-muted-foreground">— George R.R. Martin</p>
              </div>

              <p className="text-xl text-foreground/80 mb-8">
                O livro está esperando. A primeira página está pronta para ser virada. O conhecimento acumulado de milhares de anos de civilização humana está ao seu alcance. A única pergunta que resta é: você vai começar hoje?
              </p>

              <div className="bg-gradient-to-r from-coral/10 via-primary/10 to-accent/20 p-8 rounded-2xl border">
                <p className="text-foreground/80 mb-4">
                  <strong>Lembre-se:</strong> Ler 52 livros por ano não é sobre provar nada para ninguém. É sobre investir em você mesmo, expandir sua mente, e se tornar uma versão melhor de quem você é hoje. Cada página que você lê é um tijolo na construção da pessoa que você está se tornando.
                </p>
                <p className="text-foreground/80">
                  Pegue um livro. Leia 20 páginas. Repita amanhã. E no dia seguinte. Em um ano, você vai olhar para trás e não vai acreditar quão longe chegou.
                </p>
              </div>
            </section>

            <div className="my-16"><CTACard variant="primary" /></div>

          </article>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default Leia52LivrosPorAno;
